import clsx from 'clsx';
import { useAtom } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import useAdmin from '../../hooks/useAdmin';
import useNavHelper from '../../hooks/useNavHelper';
import useScroll from '../../hooks/useScroll';
import {
  colorManagerAtom,
  disableNavBarAtom,
  showSearchAtom,
  slimNavAtom,
} from '../../store/uiStore';
import SearchInput, { SearchIcon } from '../search/SearchInput';
import SearchOverlay from './SearchOverlay';
import DTSLogo from './icons/DTSLogo';
import { FooterLogo } from './Footer';
import useResponsive from '@/hooks/useResponsive';
import useBrowserCompat from '@/hooks/useBrowserCompat';
interface NavBarMobileProps {
  gradient?: boolean;
  disableSearchOverlay?: boolean;
}

const NavBarMobile = ({ gradient = false, disableSearchOverlay = false }: NavBarMobileProps) => {
  const { showStickyNav } = useNavHelper();
  const [colorManager] = useAtom(colorManagerAtom);
  const [showSearch, setShowSearch] = useAtom(showSearchAtom);
  const [disableNavBar] = useAtom(disableNavBarAtom);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { navHeight } = useResponsive();
  const browserCompat = useBrowserCompat();

  if (disableNavBar) {
    return null;
  }

  useEffect(() => {
    if (showSearch) {
      const container = searchContainerRef.current;
      const input = container?.getElementsByTagName('input')?.[0];

      // For desktop and Android web browsers, focus the input when the search is opened.
      // Don't do that on iOS, because it causes the view to zoom in on the input immediately.
      if (!browserCompat?.isSafari || !browserCompat?.isTouchDevice) {
        input?.focus();
      }
    }
  }, [showSearch]);

  return (
    <>
      <div
        className={clsx(
          'sticky top-0 dts-fullwidth w-[100vw] dts-fullpadded py-[20px] z-50 flex flex-col gap-[20px] duration-300 transition-[transform,_background-color] ease-out will-change-transform',
          gradient && !showSearch ? 'bg-gradient-to-b from-black' : colorManager.background,
          !showSearch && 'border-b border-white',
          colorManager?.text ? colorManager.text : 'text-white'
        )}
        style={{
          transform: showStickyNav
            ? 'translate3d(0, 0%, 0)'
            : 'translate3d(0, calc(-100% - 1px), 0)',
          height: navHeight - 1 + 'px', // The -1 is to account for the border
        }}
      >
        <div className="w-full flex justify-between align-center">
          <div className="w-[64px] flex align-center">
            <Link href="/" className="contents">
              <FooterLogo fill={colorManager.text === 'text-black' ? 'black' : 'white'} />
            </Link>
          </div>
          {showSearch ? (
            <>
              <div
                ref={searchContainerRef}
                className="absolute right-[20px] xs:right-[84px] z-50 w-[calc(100vw_-_40px)] xs:w-[calc(100vw_-_168px)] top-[calc(35vh_-_40px)]"
              >
                <SearchInput fullWidth noClose onShowSuggestions={setShowSuggestions} />
              </div>

              <div
                className="flex flex-row-reverse gap-2 items-center"
                onClick={() => setShowSearch(false)}
              >
                <p className="font-nhg font-normal text-sm leading-none tracking-[0.15rem] w-fit underline underline-offset-4 uppercase my-auto">
                  Close
                </p>
              </div>
            </>
          ) : (
            <div
              className="flex flex-row-reverse gap-2 items-center"
              onClick={() => setShowSearch(true)}
            >
              <p className="font-nhg font-normal text-sm 3xl:text-sm 4xl:text-[15px] leading-none opacity-50 w-fit">
                Search our images
              </p>
              <div className="pt-[2px]">
                <SearchIcon fill={colorManager?.text === 'text-white' ? 'white' : 'black'} />
              </div>
            </div>
          )}
        </div>
        <div className="w-full flex justify-between">
          <NavItem
            title="Projects"
            link="/projects"
            iconUrl="/assets/images/nav-projects.png"
            activePaths={['/project']}
          />
          <NavItem title="Explore" link="/explore" iconUrl="/assets/images/nav-explore.png" />
          <NavItem
            title="Creators"
            link="/collective"
            iconUrl="/assets/images/nav-creators.png"
            activePaths={['/creator', '/collection/collective']}
          />
          <NavItem
            title="Moodboard"
            link="/moodboard"
            iconUrl="/assets/images/nav-moodboard.png"
            activePaths={['/collection']}
          />
          <NavItem title="Account" link="/account" iconUrl="/assets/images/nav-account.png" />
        </div>
      </div>
      {<SearchOverlay hideTags={showSuggestions} />}
    </>
  );
};

const NavItem = ({
  title,
  link,
  iconUrl,
  activePaths = [],
}: {
  title: string;
  link: string;
  iconUrl: string;
  activePaths?: string[];
}) => {
  const [colorManager] = useAtom(colorManagerAtom);
  const [showSearch, setShowSearch] = useAtom(showSearchAtom);
  const [colorClasses, setColorClasses] = useState('');

  const router = useRouter();
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    const pathMatch = [...activePaths, link]?.some((path) => router.pathname.startsWith(path));
    setIsActive(pathMatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  useEffect(() => {
    if (isActive) {
      if (colorManager.navActive) {
        setColorClasses('text-white border-transparent');
      } else {
        setColorClasses('text-orange-400 border-orange-400');
      }
    }
  }, [colorManager, isActive]);

  const handleShowSearch = () => {
    if (showSearch) {
      setShowSearch(false);
    }
  };

  return (
    <Link href={link} className="contents">
      <div className="flex flex-col items-center justify-center">
        <Image src={iconUrl} alt={title} width={32} height={32} />
        <p className={clsx('text-xs xs:text-sm', isActive && colorClasses)}>{title}</p>
      </div>
    </Link>
  );
};

export default NavBarMobile;
